import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { CheckIcon, PhoneIcon, ArrowRightIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { ContactForm } from '../components/ContactForm';
import { CTABand } from '../components/CTABand';
import { getService, services } from '../data/services';
import { site } from '../data/site';
import { counties } from '../data/serviceAreas';
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace data-id="element-450" data-mp="1cqkmt966u669" />;
  const related = services.filter(s => s.slug !== service.slug && s.category === service.category).slice(0, 3);
  const phone = service.usePlumbingPhone ? site.plumbingPhone : site.estimatePhone;
  return <>
      <PageHero eyebrow={service.category} title={service.name} description={service.summary} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Services',
      to: '/services'
    }, {
      label: service.name
    }]} image={service.image} data-id="element-451" data-mp="3sl2ycfhjapak" />

      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-12 lg:grid-cols-[1.6fr_1fr] md:py-16" data-id="element-452" data-mp="1dfb8ih034dz9">
        <article data-id="element-453" data-mp="ziea9uhmzu8c">
          {service.image && <img src={service.image} alt={service.imageAlt ?? `${service.name} service`} className="mb-8 aspect-[16/9] w-full rounded-sm border border-steel-200 object-cover object-center" data-id="element-454" data-mp="13h5bacp83w0q" />}
          {service.intro.map(p => <p key={p} className="mb-4 text-lg leading-relaxed text-ink-600 first:text-xl" data-id="element-455" data-mp="fsud5hawwag5">
              {p}
            </p>)}

          <div className="mt-10 space-y-8" data-id="element-456" data-mp="6rw9j04dy4y3">
            {service.blocks.map(block => <section key={block.title} data-id="element-457" data-mp="bqr1arkbz3uc">
                <h2 className="border-b-2 border-safety pb-2 font-display text-2xl font-bold uppercase tracking-tight text-ink" data-id="element-458" data-mp="bppejptycsws">
                  {block.title}
                </h2>
                {block.body && <p className="mt-3 text-base leading-relaxed text-steel-500" data-id="element-459" data-mp="28nn1voqp6hlz">
                    {block.body}
                  </p>}
                {block.bullets && <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-460" data-mp="1zpt3875gnbvn">
                    {block.bullets.map(b => <li key={b} className="flex items-start gap-2.5 rounded-sm border border-steel-200 bg-white px-3.5 py-2.5 text-sm font-medium text-ink-700" data-id="element-461" data-mp="2wdzf9f4pp6jy">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-safety" aria-hidden="true" data-id="element-462" data-mp="2if9jq3xjjexp" />
                        {b}
                      </li>)}
                  </ul>}
              </section>)}
          </div>

          {related.length > 0 && <section className="mt-12" data-id="element-463" data-mp="3ilczxr9xyehr">
              <h2 className="font-display text-xl font-semibold uppercase tracking-tight text-ink" data-id="element-464" data-mp="3nlhehbragz5c">
                Related services
              </h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-465" data-mp="3rm1dkkvfacu4">
                {related.map(r => <Link key={r.slug} to={`/services/${r.slug}`} className="group rounded-sm border border-steel-200 bg-white p-4 transition hover:border-safety" data-id="element-466" data-mp="b1ey52dd3fu">
                    <p className="font-display text-lg font-semibold uppercase tracking-tight text-ink" data-id="element-467" data-mp="f67gaeqise2u">
                      {r.name}
                    </p>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-safety" data-id="element-468" data-mp="2gxu281vk1isg">
                      View
                      <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-469" data-mp="2l07aawxrxsse" />
                    </span>
                  </Link>)}
              </div>
            </section>}
        </article>

        <aside className="space-y-6 lg:sticky lg:top-32 lg:self-start" data-id="element-470" data-mp="1lndysmjqk3qo">
          <div className="rounded-sm border border-steel-200 bg-white p-6 shadow-card" data-id="element-471" data-mp="1zm40kfhi2rld">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-safety" data-id="element-472" data-mp="1updk3r1s9tzf">
              {phone.label}
            </p>
            <a href={phone.href} className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-ink hover:text-safety" data-id="element-473" data-mp="16z14rv47kuyt">
              <PhoneIcon className="h-6 w-6 text-safety" aria-hidden="true" data-id="element-474" data-mp="1gq5g8kuodxow" />
              {phone.display}
            </a>
            <p className="mt-3 text-sm leading-relaxed text-steel-500" data-id="element-475" data-mp="1bvrm0lrr1wlt">
              {service.usePlumbingPhone ? 'Call for plumbing service, including emergency plumbing work.' : 'Free estimates and free consultations on every project.'}
            </p>
            <a href={site.emailHref} className="mt-4 block break-all text-sm font-semibold text-ink-700 hover:text-safety" data-id="element-476" data-mp="2uv7l8luog6sk">
              {site.email}
            </a>
          </div>

          <div className="rounded-sm border border-steel-200 bg-white p-6 shadow-card" data-id="element-477" data-mp="2pxfiewshv09l">
            <h2 className="font-display text-lg font-semibold uppercase tracking-tight text-ink" data-id="element-478" data-mp="2pv31mmyh2vy4">
              Service area
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-steel-500" data-id="element-479" data-mp="3imd1v38fz33x">
              {counties.join(', ')} Counties, Michigan.
            </p>
            <Link to="/service-area" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-safety hover:underline" data-id="element-480" data-mp="34og0zumygajg">
              City list
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-481" data-mp="1y69qh3mxg8ve" />
            </Link>
          </div>

          <ContactForm compact data-id="element-482" data-mp="3prsoyum8cuv" />
        </aside>
      </div>

      <CTABand data-id="element-483" data-mp="3p9mb9e8oi98" />
    </>;
}